import type { Post } from '@/types/posts'

export interface ThreadPosition {
    isGrouped: boolean
    position: 'first' | 'middle' | 'last'
    /** The previous post in the group is this post's direct parent */
    parentIsAbove: boolean
    /** Grouped but parent is NOT the previous post (skipped posts) */
    hasGapAbove: boolean
    /** Thread root URI for "View full thread" navigation (always set for grouped posts) */
    threadRootUri: string
}

/**
 * Determine if consecutive posts A (index i) and B (index i+1) belong in the same thread group.
 *
 * Rules:
 * 1. B.replyParentUri === A.uri  (direct reply chain)
 * 2. Same replyRootUri (or A is the root) AND same author
 *
 * Reposts and posts with replyParentNotFound always break grouping.
 */
function shouldGroup(a: Post, b: Post): boolean {
    if (a.isRepost || b.isRepost) return false
    // A pinned entry occupies a standalone slot, so it never joins a thread run.
    // It also shares its URI with the chronological copy (always on Mastodon,
    // and on a later AT page), and this map is URI-keyed — grouping it would let
    // one entry's position overwrite the other's.
    if (a.pinnedEntry || b.pinnedEntry) return false
    if (b.replyParentNotFound) return false

    // Rule 1: direct reply chain
    if (b.replyParentUri === a.uri) return true

    // Rule 2: same root + same author
    if (!a.author?.did || !b.author?.did) return false
    if (a.author.did !== b.author.did) return false

    // B must be a reply to something in the same thread
    if (!b.replyRootUri) return false

    // A's root is either its own replyRootUri, or A itself if A is the root
    const aRoot = a.replyRootUri ?? a.uri
    return b.replyRootUri === aRoot
}

/**
 * Walk through a flat list of posts and compute thread grouping positions.
 * Returns a Map keyed by post URI.
 */
export function computeThreadPositions(posts: Post[]): Map<string, ThreadPosition> {
    const result = new Map<string, ThreadPosition>()
    if (posts.length === 0) return result

    // First pass: find group boundaries
    // groups[i] = true means post[i] and post[i+1] are in the same group
    const linked: boolean[] = new Array(posts.length).fill(false)
    for (let i = 0; i < posts.length - 1; i++) {
        linked[i] = shouldGroup(posts[i], posts[i + 1])
    }

    // Second pass: assign positions
    let i = 0
    while (i < posts.length) {
        // Find the extent of this group
        let groupEnd = i
        while (groupEnd < posts.length - 1 && linked[groupEnd]) {
            groupEnd++
        }

        const groupSize = groupEnd - i + 1
        if (groupSize === 1) {
            // Not grouped
            i++
            continue
        }

        // Assign positions for each member of the group
        for (let j = i; j <= groupEnd; j++) {
            const post = posts[j]
            const prev = j > i ? posts[j - 1] : undefined
            const isFirst = j === i
            const isLast = j === groupEnd

            const parentIsAbove = !isFirst && post.replyParentUri === prev?.uri
            const hasGapAbove = !isFirst && !parentIsAbove

            const threadRootUri = post.replyRootUri ?? posts[i].uri

            result.set(post.uri, {
                isGrouped: true,
                position: isFirst ? 'first' : isLast ? 'last' : 'middle',
                parentIsAbove,
                hasGapAbove,
                threadRootUri,
            })
        }

        i = groupEnd + 1
    }

    return result
}
